import {Injectable} from '@angular/core';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material/snack-bar';
import {take} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {HelpersService} from './helpers.service';
import {Router} from '@angular/router';
import {ComponentType} from '@angular/cdk/portal';
import {TranslateService} from "@ngx-translate/core";
import {marker} from "@biesbjerg/ngx-translate-extract-marker";

@Injectable({
  providedIn: 'root',
})
export class NotificationService {

  config: MatSnackBarConfig = {
    duration: 8000,
    horizontalPosition: 'center',
    verticalPosition: 'top',
  };

  constructor(public snackBar: MatSnackBar,
              private router: Router,
              private translateService: TranslateService,
  ) {
  }

  success(msg) {
    this.scheduleDisplay(msg, this.showSuccess.bind(this));
  }

  private showSuccess(msg) {
    this.config.panelClass = ['success-notification'];
    if (typeof msg === 'string') {
      this.openCloseableMessage(msg, this.config);
    } else {
      msg.pipe(
        take(1),
      )
        .subscribe((response) => {
          this.openCloseableMessage(response, this.config);
        });
    }
  }

  error(msg) {
    this.scheduleDisplay(msg, this.showError.bind(this));
  }

  warningFromComponent(component: ComponentType<any>, data: any) {
    this.config.panelClass = ['warning-notification'];
    this.config.data = data;
    this.scheduleDisplay(component, this.showMessageFromComponent(component));
  }

  private showError(msg) {
    this.config.panelClass = ['error-notification'];
    if (msg instanceof HttpErrorResponse) {
      const wrappedError = HelpersService.wrapError(msg);
      this.openCloseableMessage(wrappedError.errorMessage, this.config);
    } else if (msg['errorMessage'] !== null && msg['errorMessage'] !== undefined) {
      // was already wrapped
      this.openCloseableMessage(msg['errorMessage'], this.config);
    } else if (typeof msg === 'string') {
      this.openCloseableMessage(msg, this.config);
    } else {
      msg.pipe(
        take(1),
      )
        .subscribe((response) => {
          this.snackBar.open(response, 'Close', this.config);
        });
    }
  }

  private showMessageFromComponent(component: ComponentType<any>) {
    this.snackBar.openFromComponent(component, this.config);
  }

  warn(msg) {
    this.scheduleDisplay(msg, this.showWarn.bind(this));
  }

  private showWarn(msg) {
    this.config.panelClass = ['warning-notification'];
    if (typeof msg === 'string') {
      this.openCloseableMessage(msg, this.config);
    } else {
      msg.pipe(
        take(1),
      )
        .subscribe((response) => {
          this.snackBar.open(response, 'Close', this.config);
        });
    }
  }

  /**
   * Shows a warning immediately, without waiting for prev scheduled notifications to be displayed.
   * The warning will stay until the Close button is clicked.
   */
  public showWarnUntilClicked(msg) {
    const config: MatSnackBarConfig = {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['warning-notification'],
    };


    if (typeof msg === 'string') {
      this.openCloseableMessage(msg, this.config);
    } else {
      msg.pipe(
        take(1),
      )
        .subscribe((response) => {
          this.openCloseableMessage(response, config);
        });
    }
  }


  info(msg) {
    this.scheduleDisplay(msg, this.showInfo.bind(this));
  }

  quickInfo(msg) {
    this.showInfo(msg);
  }


  private showInfo(msg) {
    this.config.panelClass = ['info-notification'];
    if (typeof msg === 'string') {
      this.openCloseableMessage(msg, this.config);
    } else {
      msg.pipe(
        take(1),
      )
        .subscribe((response) => {
          this.openCloseableMessage(response, this.config);
        });
    }
  }

  callAlert() {
    const configAlert: MatSnackBarConfig = {
      duration: 10000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['Phone', 'is-animating', 'call-notification'],
    };
    if (this.router.url !== '/viba/agent/agent-live') {
      this.snackBar.open('', '       ', configAlert)
        .onAction()
        .subscribe(() => {
          this.router.navigate(['/viba/agent/agent-live']).then(() => {
          });
        });
    }
  }

  private scheduleDisplay(msg: any, showFunction: any) {
    const openedSnackBarRef = this.snackBar._openedSnackBarRef;
    if (openedSnackBarRef !== null) {
      openedSnackBarRef.afterDismissed().subscribe(next => {
        if (showFunction) {
          showFunction(msg);
        }
      });
    } else {
      showFunction(msg);
    }
  }

  dismiss(): void {
    this.snackBar.dismiss();
  }

  private openCloseableMessage(message: string, config?: MatSnackBarConfig){
    this.translateService.get(marker('Close')).subscribe( ret => {
      this.snackBar.open(message, ret, config);
    })
  }

}
